import { AppType } from "client/Api/Apps/common";
import {
	Button,
	FormItem,
	InputRow,
	Select,
	SelectItem,
	ValidationError
} from "client/Components";
import { AppDestinationWindowSelectFormItem } from "client/Features/Apps/AppDestinationWindowSelectFormItem";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	AlertManager,
	useAlerts,
	useUnmountedCancellation
} from "client/Hooks";
import { createValidationConfiguration } from "client/Validation";
import { isNotEmpty, isNotNullNorUndefined } from "client/Validation/rules";
import {
	forwardRef,
	ReactElement,
	Ref,
	useCallback,
	useEffect,
	useImperativeHandle,
	useMemo,
	useState
} from "react";
import {
	isCancelledError
} from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import { EditAppHandle } from "../EditApp";
import {
	EditAppBase,
	EditAppSettings,
	initialGenericState
} from "../EditAppBase";
import { useSaveApp, EditAppSavedResult } from "../useSaveApp";
import {
	TopDeskConnectionSelectOptionsModel,
	getTopDeskConnections
} from "client/Api/Apps/EditApp/appParameters";
import { openEditDialog } from "client/Features/Settings/Connections/TopDesk/EditTopDeskConnectionDialog";
import { isNil } from "client/Misc/utils";
import {
	EditTopDeskAppViewModel,
	SaveTopDeskAppModel,
	TopDeskConnectionIdentifierViewModel,
	saveTopDeskApp
} from "client/Api/Apps/EditApp/saveApp";

export type EditTopDeskAppProps = {
	initialSettings: EditAppSettings<EditTopDeskAppViewModel>;
	initiallyActivateWhoHasAccessTab: boolean;
};

export const EditTopDeskApp = forwardRef(
	(
		props: EditTopDeskAppProps,
		ref: Ref<EditAppHandle>
	): ReactElement => {
		const {
			initialSettings,
			initiallyActivateWhoHasAccessTab
		} = props;

		const alertManager: AlertManager = useAlerts();
		const globalCancellation: AbortSignal = useUnmountedCancellation();
		const [formData, setFormData] = useState<SaveTopDeskAppModel>(
			() => convertToSaveModel(initialSettings)
		);

		const [optionsModel, setOptionsModel] = useState<TopDeskConnectionSelectOptionsModel>({
			canCreateNew: false,
			options: []
		});

		const {
			app,
			usersAndUserGroupsData
		} = initialSettings.model;

		const formHandler: IFormHandler<SaveTopDeskAppModel> = useFormHandler(
			formData,
			setFormData,
			validation
		);

		const connectionOptions = useMemo(
			(): readonly SelectItem<TopDeskConnectionIdentifierViewModel>[] => {
				return optionsModel.options
					.map(
						o => ({
							value: o.isDefault
								? {
									isDefault: true
								}
								: {
									id: o.id,
									isDefault: false
								},
							text: o.name
						})
					);
			},
			[optionsModel.options]
		);

		const loadTopDeskConnections = useCallback(
			async (signal: AbortSignal): Promise<void> => {
				try {
					const optionsModel: TopDeskConnectionSelectOptionsModel = await getTopDeskConnections(
						signal
					);

					setOptionsModel(optionsModel);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}
					alertManager.showError(localize("_GeneralErrorMessage"));
				}
			},
			[alertManager]
		);

		const addConnectionHandler = useCallback(
			(): void => {
				const newConnectionAddedHandler = async (id: number): Promise<void> => {
					await loadTopDeskConnections(globalCancellation);
					formHandler.for("connection")
						.attachToSelect()
						.onChange({
							id,
							isDefault: false
						});
				};

				openEditDialog(newConnectionAddedHandler);
			},
			[
				formHandler,
				globalCancellation,
				loadTopDeskConnections
			]
		);

		useEffect(
			() => {
				const cancellation = new AbortController();
				loadTopDeskConnections(cancellation.signal);

				return () => {
					cancellation.abort();
				};
			},
			[loadTopDeskConnections]
		);

		const save: () => Promise<EditAppSavedResult> = useSaveApp<SaveTopDeskAppModel>(
			formHandler,
			saveTopDeskApp,
			AppType.TopDesk
		);

		useImperativeHandle(
			ref,
			(): EditAppHandle => ({
				save
			}),
			[save]
		);

		const hasValidationErrorForSpecificSettings: boolean | undefined = formHandler
			.under("connection", "destinationWindow")
			.attachToTab()
			.hasValidationError;

		return (
			<EditAppBase
				formHandler={formHandler}
				hasValidationErrorForSpecificSettings={hasValidationErrorForSpecificSettings}
				initialAppSettings={app}
				usersAndUserGroupsData={usersAndUserGroupsData}
				initiallyActivateWhoHasAccessTab={initiallyActivateWhoHasAccessTab}
			>
				<FormItem label={localize("_Connection")}>
					<InputRow>
						<InputRow.Stretch>
							<Select
								{...formHandler.for("connection").attachToSelect()}
								options={connectionOptions}
								areEqual={checkConnectionsEqual}
								placeholder={localize("_SelectConnection")}
								menuTitle={localize("_Connection")}
							/>
						</InputRow.Stretch>
						{optionsModel.canCreateNew && (
							<Button
								text={localize("_Add")}
								onClick={addConnectionHandler}
								kind="secondary"
							/>
						)}
					</InputRow>
					<ValidationError
						{...formHandler.for("connection").attachToValidationError()}
					/>
				</FormItem>
				<AppDestinationWindowSelectFormItem
					attachable={formHandler.for("destinationWindow")}
				/>
			</EditAppBase>
		);
	}
);
EditTopDeskApp.displayName = "EditTopDeskApp";

function checkConnectionsEqual(
	a: TopDeskConnectionIdentifierViewModel | nil,
	b: TopDeskConnectionIdentifierViewModel | null
): boolean {
	if (isNil(a) || isNil(b)) {
		return false;
	}

	if (a.isDefault && b.isDefault) {
		return true;
	}

	if (
		a.isDefault === false
			&& b.isDefault === false
			&& a.id === b.id
	) {
		return true;
	}

	return false;
}

const validation = createValidationConfiguration<SaveTopDeskAppModel>();

validation.forScalar("name")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty()
	);

validation.forObject("connection")
	.add(
		isNotNullNorUndefined()
	);

function convertToSaveModel(
	initialSettings: EditAppSettings<EditTopDeskAppViewModel>
): SaveTopDeskAppModel {
	const {
		destinationWindow,
		connection
	} = initialSettings
		.model
		.app;

	return {
		...initialGenericState(initialSettings),
		destinationWindow,
		connection
	};
}